/** @format */

import { Injectable } from "@angular/core";
import { NsWISConfigurations } from "../models/configurations.model";
import { BehaviorSubject, Observable, ReplaySubject, Subject } from "rxjs";
import { NsEmpWebExpData } from "../models/emp-web-exp.model";
import { ApiService } from "./api.service";
import { parseZone } from "moment";
import { EmpUserService } from "./emp-user.service";
import { FormControl } from "@angular/forms";
import { ICommuteChoice } from "../components/forms/commute/commute.component";
import { ConfigurationService } from "./configuration.service";

type IFormData = [NsEmpWebExpData.ICommuteOption[], NsEmpWebExpData.ICommuteOption[], ICommute?];
interface ICommute {
  login?: NsEmpWebExpData.ICommuteOption;
  logout?: NsEmpWebExpData.ICommuteOption;
  registrationNumber: string;
}

@Injectable({
  providedIn: "root"
})
export class CommuteService {
  private formData$ = new ReplaySubject<IFormData>(1);
  formData = this.formData$.asObservable();
  private commuteChange$ = new BehaviorSubject<[NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?]>([]);
  commuteChange = this.commuteChange$.asObservable();
  loginOptions: NsEmpWebExpData.ICommuteOption[] = [];
  logoutOptions: NsEmpWebExpData.ICommuteOption[] = [];
  private parkingRefreshStream$ = new ReplaySubject<Observable<NsEmpWebExpData.IParkingPremise[]>>(1);
  parkingRefreshStream = this.parkingRefreshStream$.asObservable();
  regNo = new FormControl("", []);
  temporaryCommuteSelection: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?] = [];
  currentChoice: ICommuteChoice;
  shiftsValid = true;
  gridUrlData$ = new BehaviorSubject<any>(null);
  gridUrlData = this.gridUrlData$.asObservable();
  vehicleType$ = new BehaviorSubject<string>(null);
  vehicleType = this.vehicleType$.asObservable();
  allotedParkingDetails$ = new BehaviorSubject<NsEmpWebExpData.IAllotedParkingDetails>(null);
  allotedParkingDetails = this.allotedParkingDetails$.asObservable();
  enableGridFloorPlan = false;
  constructor(private apiSvc: ApiService, private userSvc: EmpUserService, private configSvc: ConfigurationService) {}

  async setFormData(
    profile: NsEmpWebExpData.IEmployee,
    configs: NsWISConfigurations.IConfigurations,
    formData: any,
    date: number,
    tz: string,
    oldCommute?: ICommute
  ): Promise<void> {
    this.shiftsValid = formData.startShift && formData.endShift;
    if (!this.shiftsValid) {
      return;
    }
    const req = {
      empGuid: profile.empGuid,
      endTime: date,
      gender: Number(profile.gender) === 0 ? "Male" : "Female",
      officeName: profile.officeLocation,
      projectId: profile.projectId,
      startTime: date
    };
    const time = offsetInMins => {
      if (!offsetInMins) {
        return parseZone().tz(tz).startOf("day").valueOf().toString();
      }
      const dateStart = parseZone(date).tz(tz).startOf("day");
      return dateStart.add(offsetInMins, "minutes").valueOf().toString();
    };
    this.setParkingStream(
      this.apiSvc.getParking(formData.office.guid, time(formData.startShift?.timeInMinutes), time(formData.endShift?.timeInMinutes)),
      oldCommute.registrationNumber,
      configs.flattenedSettings.enableGridFloorPlan
    );
    this.gridUrlData$.next(formData);
    const res = await this.apiSvc.validateShift([req], formData.startShift?.shiftId || "", formData.endShift?.shiftId || "", tz).toPromise();
    this.loginOptions = await this.genCommuteOptions(configs, formData.startShift, res?.login?.[this.userSvc.employeeInfo.empGuid] || false);
    this.logoutOptions = await this.genCommuteOptions(configs, formData.endShift, res?.logout?.[this.userSvc.employeeInfo.empGuid] || false);
    this.formData$.next([this.loginOptions, this.logoutOptions, oldCommute]);
  }

  setCommute(opt: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?]): void {
    this.commuteChange$.next(opt);
  }

  private async genCommuteOptions(
    configs: NsWISConfigurations.IConfigurations,
    shift: NsEmpWebExpData.IShiftData,
    validity: boolean
  ): Promise<NsEmpWebExpData.ICommuteOption[]> {
    return [
      {
        name: "Office Cab",
        id: "transport",
        disable: !validity || !shift?.transportAvalability || !configs?.flattenedSettings.showCabs
      },
      {
        name: "None",
        disable: false,
        id: "none"
      }
    ];
  }

  setParkingStream(obs: Observable<NsEmpWebExpData.IParkingPremise[]>, regNo: string, parkingFloorPlan?: boolean): void {
    this.parkingRefreshStream$.next(obs);
    this.regNo.patchValue(regNo);
    this.enableGridFloorPlan = parkingFloorPlan;
  }

  setAllotedParkingData(data: NsEmpWebExpData.IAllotedParkingDetails) {
    this.allotedParkingDetails$.next(data);
  }
}
